import { QueryClient, type QueryKey } from '@tanstack/vue-query'

import { ApiError } from './http'

/**
 * TanStack Query defaults: data stays fresh for 30 s, no refetch on window focus (legacy never refetched), and only
 * network or 5xx failures are retried once — a 4xx is an answer, not a glitch.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) =>
          failureCount < 1 && error instanceof ApiError && (error.isNetworkError || error.status >= 500),
      },
      mutations: { retry: false },
    },
  })
}

/** `[module, resource, params?]` (ARCHITECTURE.md §6). */
export function queryKey(module: string, resource: string, params?: unknown): QueryKey {
  return params === undefined ? [module, resource] : [module, resource, params]
}

/**
 * Resources per module, declared by each module next to its queries — no shared file to edit:
 *
 *   declare module '@/api/query' {
 *     interface QueryKeyRegistry { employees: 'list' | 'detail' }
 *   }
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- filled by declaration merging
export interface QueryKeyRegistry {}

export type ModuleKeys<R extends string> = { readonly [K in R]: (params?: unknown) => QueryKey } & {
  /** `[module]` — invalidates everything the module cached. */
  readonly all: () => QueryKey
}

export type QueryKeys = { readonly [M in keyof QueryKeyRegistry]: ModuleKeys<QueryKeyRegistry[M] & string> }

/** `qk.<module>.<resource>(params)` → `[module, resource, params]`; `qk.<module>.all()` → `[module]`. */
export const qk = new Proxy({} as QueryKeys, {
  get: (_target, module) =>
    typeof module === 'string'
      ? new Proxy(
          {},
          {
            get: (_inner, resource) =>
              typeof resource !== 'string'
                ? undefined
                : resource === 'all'
                  ? () => [module]
                  : (params?: unknown) => queryKey(module, resource, params),
          },
        )
      : undefined,
})
